/** URL query keys reserved for pagination (avoid colliding with custom filter field names). */
export const QUERY_FOLDERS_URL_PAGE = 'page';
export const QUERY_FOLDERS_URL_ITEMS_PER_PAGE = 'ipp';

function serializeValue(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? '1' : '0';
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

function parseValue(raw: string, template: unknown): unknown {
  if (typeof template === 'boolean') {
    return raw === '1' || raw === 'true';
  }
  return raw;
}

function sameAsDefault(value: unknown, def: unknown): boolean {
  if (value === def) return true;
  if (typeof value === 'string' && typeof def === 'string') {
    return value.trim() === def.trim();
  }
  return false;
}

/** Build search params for the current query-folder UI (omits values equal to `empty` defaults). */
export function serializeQueryFoldersSearchParams(
  filters: Record<string, unknown>,
  page: number,
  itemsPerPage: number,
  empty: Record<string, unknown>
): URLSearchParams {
  const p = new URLSearchParams();

  if (page > 1) {
    p.set(QUERY_FOLDERS_URL_PAGE, String(page));
  }
  if (itemsPerPage !== 10) {
    p.set(QUERY_FOLDERS_URL_ITEMS_PER_PAGE, String(itemsPerPage));
  }

  for (const key of Object.keys(empty)) {
    const val = filters[key];
    const def = empty[key];
    if (sameAsDefault(val, def)) {
      continue;
    }
    if (val === '' && (def === '' || def === undefined)) {
      continue;
    }
    p.set(key, serializeValue(val));
  }

  return p;
}

/** Merge URL search params onto `empty` defaults. */
export function parseQueryFoldersSearchParams(
  searchParams: URLSearchParams,
  empty: Record<string, unknown>
): {
  filters: Record<string, unknown>;
  page: number;
  itemsPerPage: number;
} {
  const filters: Record<string, unknown> = { ...empty };

  const pageRaw = searchParams.get(QUERY_FOLDERS_URL_PAGE);
  const ippRaw = searchParams.get(QUERY_FOLDERS_URL_ITEMS_PER_PAGE);
  const page =
    pageRaw != null && Number.isFinite(Number(pageRaw)) && Number(pageRaw) > 0
      ? Math.floor(Number(pageRaw))
      : 1;
  const itemsPerPage =
    ippRaw != null && Number.isFinite(Number(ippRaw)) && Number(ippRaw) > 0
      ? Math.floor(Number(ippRaw))
      : 10;

  searchParams.forEach((rawValue, key) => {
    if (
      key === QUERY_FOLDERS_URL_PAGE ||
      key === QUERY_FOLDERS_URL_ITEMS_PER_PAGE
    ) {
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(empty, key)) {
      return;
    }
    const template = empty[key];
    filters[key] = parseValue(rawValue, template);
  });

  return { filters, page, itemsPerPage };
}
